import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { firePixelEvent } from '../pixels';
import { track } from '../services/track';
import { getStats, getLeaderboard } from '../services/api';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: null,
    badge: null,
    desc: 'Full cognitive combine + shareable profile.',
    features: [
      { label: 'Complete Cognitive Combine', included: true },
      { label: 'Shareable Neural Profile', included: true },
      { label: 'Leaderboard Access', included: true },
      { label: 'Practice Mode', included: true },
      { label: 'Shortlist up to 10 profiles', included: true },
      { label: 'Scout Dashboard', included: false },
      { label: 'Advanced Filters & Compare', included: false },
      { label: 'CSV Export', included: false },
      { label: 'Saved Filter Presets', included: false },
      { label: 'Compare up to 4 profiles', included: false },
    ],
    cta: 'Current Plan',
    type: null,
    color: 'var(--text-muted)',
  },
  {
    id: 'player_pro',
    name: 'Player Pro',
    price: '$9.99',
    period: '/mo',
    badge: 'Most Popular',
    desc: 'Your personal AI coach. 30 days to a sharper cognitive profile.',
    features: [
      { label: 'Everything in Free', included: true },
      { label: 'AI Coach Twin — 30-day personalized program (coming soon)', included: true },
      { label: 'Reverse Combine — unlimited clip analysis (coming soon)', included: true },
      { label: 'AI Training Plan + Deep Stats (per-trial breakdown)', included: true },
      { label: 'Pro Comparison vs rank tiers', included: true },
      { label: 'Pro Dossier side-by-side comparison (coming soon)', included: true },
      { label: 'Unlimited Re-tests + Streaming Overlay (coming soon)', included: true },
      { label: 'Completion Certificate & Premium Badge', included: true },
    ],
    cta: 'Upgrade to Player Pro',
    type: 'player_pro',
    color: '#f59e0b',
  },
  {
    id: 'scout',
    name: 'Scout Pro',
    price: '$14',
    period: '/mo',
    badge: 'For Scouts',
    desc: 'Full scout dashboard for talent discovery.',
    features: [
      { label: 'Complete Cognitive Combine', included: true },
      { label: 'Shareable Neural Profile', included: true },
      { label: 'Leaderboard Access', included: true },
      { label: 'Practice Mode', included: true },
      { label: 'Shortlist up to 50 profiles', included: true },
      { label: 'Scout Dashboard', included: true },
      { label: 'Advanced Filters & Compare (2)', included: true },
      { label: 'CSV Export', included: true },
      { label: 'Saved Filter Presets', included: false },
      { label: 'Compare up to 4 profiles', included: false },
    ],
    cta: 'Upgrade to Scout Pro',
    type: 'scout',
    color: '#00d4ff',
  },
  {
    id: 'team',
    name: 'Team Plan',
    price: '$39',
    period: '/mo',
    badge: 'For Organizations',
    desc: 'Everything in Scout Pro, built for coaching staff and orgs.',
    features: [
      { label: 'Complete Cognitive Combine', included: true },
      { label: 'Shareable Neural Profile', included: true },
      { label: 'Leaderboard Access', included: true },
      { label: 'Practice Mode', included: true },
      { label: 'Unlimited Shortlist', included: true },
      { label: 'Scout Dashboard', included: true },
      { label: 'Advanced Filters & Compare (2)', included: true },
      { label: 'CSV Export', included: true },
      { label: 'Saved Filter Presets', included: true },
      { label: 'Compare up to 4 profiles', included: true },
    ],
    cta: 'Upgrade to Team Plan',
    type: 'team',
    color: '#a855f7',
  },
];

const BOOSTS = [
  {
    id: 'featured',
    icon: '★',
    name: 'Featured Profile Boost',
    price: '$4.99',
    desc: 'Pin your profile to the top of scout search results for 30 days.',
    type: 'featured',
    color: '#f59e0b',
  },
  {
    id: 'retest',
    icon: '↻',
    name: 'Re-test Bypass Credit',
    price: '$1.99',
    desc: 'Skip the 24-hour cooldown and retake a combine immediately.',
    type: 'retest',
    color: '#10b981',
  },
];

function Toast({ message, type }) {
  return (
    <div style={{
      position: 'fixed', top: 80, right: 24, zIndex: 9999,
      background: type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
      border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
      borderRadius: 10, padding: '14px 20px',
      color: type === 'success' ? '#10b981' : '#ef4444',
      fontSize: 14, fontWeight: 600, maxWidth: 340,
      backdropFilter: 'blur(12px)',
    }}>
      {message}
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const location = useLocation();
  const [loadingType, setLoadingType] = useState(null);
  const [toast, setToast] = useState(null);
  const [featuredId, setFeaturedId] = useState('');
  const [profileCount, setProfileCount] = useState(null);
  const [sampleProfiles, setSampleProfiles] = useState([]);

  useEffect(() => {
    document.title = 'Pricing | NeuroRank';
    return () => { document.title = 'NeuroRank | Cognitive Combine'; };
  }, []);
  useEffect(() => { try { window.posthog?.capture('pricing_page_visited'); } catch {} ; track('pricing_view'); }, []);
  useEffect(() => {
    getStats().then(({ profileCount: c }) => setProfileCount(c)).catch(() => {});
    getLeaderboard({ genre: 'all', sortBy: 'overallScore', limit: 3 })
      .then(({ entries }) => setSampleProfiles(entries))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === '1') {
      setToast({ message: 'Payment successful! Your account has been updated.', type: 'success' });
    } else if (params.get('canceled') === '1') {
      setToast({ message: 'Checkout canceled. No charges were made.', type: 'error' });
    }
  }, [location.search]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const checkout = async (type, opts = {}) => {
    if (!user) {
      setToast({ message: 'Please sign in to continue.', type: 'error' });
      return;
    }
    setLoadingType(type);
    try {
      const utm = JSON.parse(sessionStorage.getItem('nr_utm') || '{}');
      try {
        window.posthog?.capture('checkout_started', { plan: type, ...utm });
        firePixelEvent('InitiateCheckout', { content_name: type });
        track('pricing_cta_click', { plan: type });
      } catch {}
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('nr_token')}`,
        },
        body: JSON.stringify({ type, ...opts }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start checkout.');
      window.location.href = data.url;
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
      setLoadingType(null);
    }
  };

  const userPlan = user?.subscription?.plan || 'free';

  return (
    <div style={{ paddingBottom: 80 }}>
      {toast && <Toast message={toast.message} type={toast.type} />}

      {/* Header */}
      <div style={{ borderBottom: '1px solid var(--border)', padding: '56px 0 48px', textAlign: 'center' }}>
        <div className="container">
          <div style={{ fontSize: 11, letterSpacing: 3, color: 'var(--accent)', fontWeight: 700, marginBottom: 14, textTransform: 'uppercase' }}>
            Pricing
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 900, marginBottom: 14 }}>Train like an esports pro.</h1>
          <p className="text-muted" style={{ maxWidth: 500, margin: '0 auto', fontSize: 15, lineHeight: 1.6 }}>
            The combine is always free. Upgrade to unlock your AI coach, track your progress, and compare yourself to the pros.
          </p>
          {profileCount != null && (
            <p style={{ marginTop: 20, fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>
              <span style={{ color: 'var(--accent)' }}>{profileCount} players</span> have taken the combine
            </p>
          )}
          {sampleProfiles.length > 0 && (
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
              {sampleProfiles.map(p => (
                <span key={p.shareableId} style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '4px 12px', borderRadius: 20, border: '1px solid var(--border)' }}>
                  {p.archetype?.name?.replace('The ', '')} · {p.scores?.overallScore}/100
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="container" style={{ paddingTop: 56 }}>

        {/* Plan Cards */}
        <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24, marginBottom: 72, paddingTop: 16 }}>
          {PLANS.map((plan) => {
            const isCurrent = userPlan === plan.id;
            const isLoading = loadingType === plan.type;
            const isHighlighted = plan.id === 'scout' || plan.id === 'player_pro';
            return (
              <div
                key={plan.id}
                style={{
                  borderRadius: 18,
                  border: `1px solid ${isHighlighted ? plan.color + '55' : 'var(--border)'}`,
                  background: isHighlighted ? `${plan.color}08` : 'var(--bg-card)',
                  padding: '32px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  marginTop: 14,
                }}
              >
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                    background: plan.color,
                    color: plan.id === 'scout' ? '#000' : '#fff',
                    fontSize: 10, fontWeight: 800, letterSpacing: 1.5,
                    padding: '4px 14px', borderRadius: 20,
                    textTransform: 'uppercase', whiteSpace: 'nowrap',
                  }}>
                    {plan.badge}
                  </div>
                )}

                <div style={{ marginBottom: 16 }}>
                  <h2 style={{ fontSize: 17, fontWeight: 800, color: plan.color, margin: '0 0 6px' }}>{plan.name}</h2>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>{plan.desc}</p>
                </div>

                <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid var(--border)' }}>
                  {plan.price ? (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: 38, fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{plan.price}</span>
                      <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{plan.period}</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 30, fontWeight: 900, color: 'var(--text-muted)' }}>Free</span>
                  )}
                </div>

                <div style={{ flex: 1, marginBottom: 28 }}>
                  {plan.features.map((f) => (
                    <div key={f.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 11 }}>
                      <span style={{
                        color: f.included ? '#10b981' : '#2a2a2a',
                        fontSize: 13, flexShrink: 0, marginTop: 1, fontWeight: 700,
                      }}>
                        {f.included ? '✓' : '—'}
                      </span>
                      <span style={{ fontSize: 13, color: f.included ? 'var(--text-secondary)' : '#3a3a3a', lineHeight: 1.4 }}>
                        {f.label}
                      </span>
                    </div>
                  ))}
                </div>

                {plan.type ? (
                  isCurrent ? (
                    <button className="btn btn--ghost" disabled style={{ width: '100%', opacity: 0.5 }}>
                      Current Plan
                    </button>
                  ) : !user ? (
                    <button
                      className="btn btn--primary"
                      style={{ width: '100%', background: plan.color, borderColor: plan.color, color: plan.id === 'scout' ? '#000' : '#fff', fontWeight: 700 }}
                      onClick={() => setToast({ message: 'Sign in first to upgrade your plan.', type: 'error' })}
                    >
                      Sign In to Upgrade
                    </button>
                  ) : (
                    <button
                      className="btn btn--primary"
                      style={{ width: '100%', background: plan.color, borderColor: plan.color, color: plan.id === 'scout' ? '#000' : '#fff', fontWeight: 700 }}
                      onClick={() => checkout(plan.type)}
                      disabled={!!loadingType}
                    >
                      {isLoading ? 'Redirecting…' : plan.cta}
                    </button>
                  )
                ) : (
                  <button className="btn btn--ghost" disabled style={{ width: '100%', opacity: 0.4 }}>
                    Free Forever
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* One-time Boosts */}
        <div id="boosts" style={{ marginBottom: 72 }}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <div style={{ fontSize: 11, letterSpacing: 3, color: 'var(--accent)', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase' }}>
              One-time
            </div>
            <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 10px' }}>Boosts</h2>
            <p className="text-muted" style={{ fontSize: 14 }}>No subscription required. Pay once, use immediately.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, maxWidth: 720, margin: '0 auto' }}>
            {BOOSTS.map((boost) => {
              const isLoading = loadingType === boost.type;
              return (
                <div
                  key={boost.id}
                  style={{
                    borderRadius: 16, border: '1px solid var(--border)',
                    background: 'var(--bg-card)', padding: '28px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                      background: `${boost.color}15`, border: `1px solid ${boost.color}33`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, color: boost.color,
                    }}>
                      {boost.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{boost.name}</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: boost.color, letterSpacing: '-0.02em' }}>{boost.price}</div>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.5 }}>{boost.desc}</p>

                  {boost.type === 'featured' && (
                    <input
                      className="input"
                      placeholder="Profile ID (e.g. NR-ABC123)"
                      value={featuredId}
                      onChange={(e) => setFeaturedId(e.target.value.toUpperCase())}
                      style={{ marginBottom: 12, width: '100%', boxSizing: 'border-box' }}
                      maxLength={9}
                    />
                  )}

                  <button
                    className="btn btn--primary"
                    style={{ width: '100%', background: boost.color, borderColor: boost.color, color: '#000', fontWeight: 700 }}
                    onClick={() => checkout(boost.type, boost.type === 'featured' ? { shareableId: featuredId } : {})}
                    disabled={!!loadingType || (boost.type === 'featured' && !/^NR-[A-Z0-9]{6}$/.test(featuredId))}
                  >
                    {isLoading ? 'Redirecting…' : `Buy for ${boost.price}`}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer note */}
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)', marginTop: 8, paddingBottom: 24 }}>
          Payments processed securely by Stripe. Cancel anytime in your{' '}
          <Link to="/billing" style={{ color: 'var(--accent)' }}>billing settings</Link>.
        </p>
      </div>
    </div>
  );
}
